import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import EngineeringIcon from '@mui/icons-material/Engineering';
import AddchartIcon from '@mui/icons-material/Addchart';
// Import MUI icons used in the menu
import AssignmentIcon from "@mui/icons-material/Assignment";
import DnsIcon from "@mui/icons-material/Dns";
import StorageIcon from "@mui/icons-material/Storage";
import TrafficIcon from "@mui/icons-material/Traffic";
import DashboardIcon from '@mui/icons-material/Dashboard';
import MainHeader from "./MainHeader";
import "./MainMenu.scss";
import logo from "../assests/Aayslogo.png";
import { Link } from "react-router-dom";
import HomeFilledIcon from '@mui/icons-material/HomeFilled';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import Tooltip from '@mui/material/Tooltip';
import { Network, LineChart, Boxes } from "lucide-react";
import axios from "axios";
import { use } from "react";

const drawerWidth = 240;
 
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});
 
const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});
 
const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open
    ? {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": {
          ...openedMixin(theme),
          backgroundColor: "#ffffff",
        },
      }
    : {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": {
          ...closedMixin(theme),
          backgroundColor: "#ffffff",
        },
      }),
}));
 
const Main = styled("main", {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "isPadding",
})(({ theme, open, isPadding }) => ({
  flexGrow: 1,
  padding: isPadding ? theme.spacing(3) : 0,
  height: "calc(100vh - 14px)",
  overflow:"hidden",
  overflowY:"auto",
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: open
      ? theme.transitions.duration.enteringScreen
      : theme.transitions.duration.leavingScreen,
  }),
}));  
 
const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between", // space between logo and icon button
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));
 
// Map icon name strings to actual icon components
const iconMap = {
  AssignmentIcon: <AssignmentIcon sx={{ fontSize: 24 }} />,
  DomainIcon: <Network size={24} />,
  StorageIcon: <Boxes size={24} />,
  TrafficIcon: <TrafficIcon sx={{ fontSize: 24 }} />,
  DashboardIcon: <DashboardIcon sx={{ fontSize: 24 }} />,
  Onboarding: <EngineeringIcon sx={{ fontSize: 24 }} />,
  HomeIcon: <HomeFilledIcon sx={{ fontSize: 24 }} />,
  LeaderboardIcon: <LineChart size={24} />,
  accessManagement: <ManageAccountsIcon sx={{ fontSize: 24 }} />,
PendingActionsIcon : < PendingActionsIcon sx={{ fontSize: 24 }} />,
};
 
export default function MainMenu({ children, text1, text2, padding }) {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
 
  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);
  const [role, setRole] = React.useState("Product Owner");
 
  // Current path for active menu detection
  const currentPath = window.location.pathname;
 
 
  const getRole = async () => {
    try {
      const userId = localStorage.getItem("userId");
      const url  = `${process.env.REACT_APP_API_URL}/user/id/${userId}`;
 
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
        },
      });
  
      // Axios automatically parses JSON, so no need for response.json()
      setRole(response.data.role_name);
    } catch (error) {
      console.error("Error fetching role:", error);
    }
  };
 
  React.useEffect(() => {
    getRole();
  })
  
 
  const fullMenuList = [
    {
        name: "Home",
        path: "/",
        icons: "HomeIcon",
        roles: ["Admin","Product Owner","Data Steward","Data Engineer","Vendor","Domain Owner"],
 
      },
    {
        name: "Overview",
        path: "/Dashboard",
        icons: "DashboardIcon",
        roles: ["Admin","Product Owner","Data Steward","Data Engineer","Vendor","Domain Owner"],
 
      },
  
    {
      name: "Domain Management",
      path: "/dataDomain",
      icons: "DomainIcon",
      roles: ["Admin","Product Owner","Data Steward","Data Engineer","Vendor","Domain Owner"],
 
    },
    {
      name: "Data Discovery",
      path: "/data-contract",
      icons: "StorageIcon",
      roles: ["Admin","Product Owner","Data Steward","Data Engineer","Vendor", "Domain Owner"],
 
    },
    {
      name: "Data Traffic Lights",
      path: "/data-traffic-lights",
      icons: "TrafficIcon",
      roles: ["Admin","Product Owner","Data Steward","Data Engineer","Vendor", "Domain Owner"],
 
    },
    {
      name: "Data Monitoring",
      path: "/data-monitoring",
      icons: "LeaderboardIcon",
      roles: ["Admin","Product Owner","Data Steward","Data Engineer","Vendor", "Domain Owner"],
 
    },
     {
      name: "Actions",
      path: "/action",
      icons: "PendingActionsIcon",
      roles: ["Admin","Product Owner","Data Steward","Data Engineer","Vendor", "Domain Owner"],
 
    },
    {
      name: "Access Management",
      path: "/accessManagement",
      icons: "accessManagement",
      roles: ["Admin"],
 
    },
    
  ];
 
  const menuList = React.useMemo(() => {
    return fullMenuList.filter(item => item.roles.includes(role));
  }, [role]);
 
 
  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
 
      {/* AppBar */}
      <MainHeader open={open} handleDrawerOpen={handleDrawerOpen} text={text1}  text2={text2} />
 
      {/* Drawer */}
      <Drawer variant="permanent" open={open}>
        <DrawerHeader
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: open ? "space-between" : "center", // center image when closed, spaced when open
            px: open ? 2 : 0,
          }}
        >
          <img
            src={logo}
            alt="logo"
            style={{ height: open ? 35 : 35 }} // 50px when open, 40px when closed
          />
 
          {open && (
            <IconButton onClick={handleDrawerClose}>
              {theme.direction === "ltr" ? (
                 <svg
      className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium"
      focusable="false"
      aria-hidden="true"
      viewBox="0 0 24 24"
      data-testid="MenuOpenIcon"
      style={{ color: "#bb1ccc", width: 24, height: 24 }}
      fill="#bb1ccc"
    >
      <path d="M3 18h13v-2H3zm0-5h10v-2H3zm0-7v2h13V6zm18 9.59L17.42 12 21 8.41 19.59 7l-5 5 5 5z"></path>
    </svg>
  ) : (
                <ChevronRightIcon sx={{ color: "black" }} />
              )}
            </IconButton>
          )}
        </DrawerHeader>
 
        <Divider />
 
        <List>
          {menuList.map(({ name, icons, path }) => {
            const isActive = currentPath === path;
            // const isComingSoon =   name === "Data Monitoring" || name === "Data Traffic Lights";
            const isComingSoon = name === "";
 
            
            if (isComingSoon) {
              // Non-clickable items with "Coming Soon" tooltip
              return (
                <ListItem key={name} disablePadding sx={{ display: "block" }}>
                  <Tooltip
                    title={!open ? "Coming Soon" : ''}
                    placement="right"
                    arrow
                   
                  >
                    <ListItemButton
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? "initial" : "center",
                        color: "#333333",
                        bgcolor: "transparent",
                        borderRadius: "10px",
                        mb: 0.5,
                        ml: 1,
                        mr: 1,
                        // cursor: "not-allowed",
                        "&:hover": {
                          bgcolor: "rgb(0, 0, 60, .4)",
                        },
                      }}
                      onClick={(e) => e.preventDefault()} // Prevent navigation
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 1.5 : "auto",
                          justifyContent: "center",
                          color: "#333333",
                        }}
                      >
                        {iconMap[icons]}
                      </ListItemIcon>
                      <ListItemText
                        primary={name}
                        sx={{
                          opacity: open ? 1 : 0,
                          color: "#333333",
                        }}
                      />
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            } else if (path === "/product-onboarding") {
              // Special handling for product onboarding
              return (
                <div
                  key={name}
                  style={{ textDecoration: "none", cursor: "pointer" }}
                  onClick={() => window.location.href = path}
                >
                  <ListItem disablePadding sx={{ display: "block" }}>
                    <Tooltip title={!open ? name : ''} placement="right" arrow
                    >
                      <ListItemButton
                        sx={{
                          minHeight: 48,
                          justifyContent: open ? "initial" : "center",
                          color: "white",
                          bgcolor: isActive ? "#fff" : "transparent",
                          borderRadius: "10px",
                          mb: 0.5,
                          ml: 1,
                          mr: 1,
                          "&:hover": {
                            bgcolor: isActive ? "#fff" : "rgb(0, 0, 60, .4)",
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 1.5 : "auto",
                            justifyContent: "center",
                            color: isActive ? "#0000a0" : "#333333",
                          }}
                        >
                          {iconMap[icons]}
                        </ListItemIcon>
                        <ListItemText
                          primary={name}
                          sx={{
                            opacity: open ? 1 : 0,
                            color: isActive ? "#0000a0" : "#333333",
                          }}
                        />
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                </div>
              );
            } else {
              // Regular clickable items
            return (
                <Link style={{textDecoration:"none"}} to={`${path}`} key={name}>
                  <ListItem disablePadding sx={{ display: "block" }}>
                  <Tooltip title={!open ? name : ''} placement="right" arrow
                  >
                    <ListItemButton
                    sx={{
  minHeight: 48,
  justifyContent: open ? "initial" : "center",
  color: "white",
  bgcolor: isActive ? "#F7E8FF" : "transparent",        
  borderLeft: isActive ? "4px solid #bb1ccc" : "4px solid transparent", 
  mb: 0.5,
  ml: 1,
  mr: 1,
  "&:hover": {
    bgcolor: isActive ? "#F7E8FF" : "rgb(0, 0, 60, .04)",  
  },
}}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 1.5 : "auto",
                          justifyContent: "center",
                          color: isActive ? "#bb1ccc" : "#333333",
                        }}
                      >
                        {iconMap[icons]}
                      </ListItemIcon>
                      <ListItemText
                        primary={name}
                        sx={{
                          opacity: open ? 1 : 0,
                          color: isActive ? "#bb1ccc" : "#333333",
                        }}
                      />
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              </Link>
            );
            }
          })}
        </List>
 
        <Divider />
      </Drawer>
 
      {/* Main content area */}
      <Main open={open} isPadding={padding}>
        <DrawerHeader />
        {children}
      </Main>
    </Box>
  );
}
 
 